import React, { useMemo } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { GripVertical, Eye, EyeOff } from "lucide-react";

const GridLayout = WidthProvider(Responsive);

/**
 * Grille de widgets avec drag & resize, base 12 colonnes.
 * Chaque widget expose `size: s|m|l` qui définit un h/w par défaut.
 *
 * Props :
 * - `catalog`      : liste des widgets (déclarative depuis backend)
 * - `enabledMap`   : { widget_key: bool }
 * - `prefs`        : { widget_key: {enabled, order, x, y, w, h} }
 * - `onLayoutChange(nextPrefs)` : callback avec la nouvelle prefs
 * - `onToggle(key)`             : toggle enabled/désactivé
 * - `readOnly`     : masque les handles + désactive drag/resize
 * - `renderBody(widget)` : optionnel — renderer custom pour le corps du widget
 *                          (utile en mode readOnly pour afficher données live).
 *                          Si non fourni, affiche label + catégorie.
 * - `testidPrefix` : préfixe testid ("chef" ou "salarie")
 */
export default function WidgetGridBuilder({
  catalog, enabledMap, prefs, onLayoutChange, onToggle, readOnly = false,
  renderBody, testidPrefix = "widget",
}) {
  const defaultSize = (size) => {
    if (size === "l") return { w: 12, h: 3 };
    if (size === "m") return { w: 6, h: 2 };
    return { w: 3, h: 2 };
  };

  // Widgets affichés = tous ceux du catalog (même désactivés → montrés en grisé en mode édition)
  const displayCatalog = useMemo(() => {
    if (readOnly) return catalog.filter((w) => enabledMap[w.key]);
    return catalog;
  }, [catalog, enabledMap, readOnly]);

  // Génère le layout react-grid-layout {i, x, y, w, h} pour chaque widget affiché.
  // Fallback : place les widgets en flux si x/y absent (respect de `order`).
  const layout = useMemo(() => {
    let cursorX = 0, cursorY = 0;
    return displayCatalog.map((w) => {
      const p = prefs[w.key] || {};
      const d = defaultSize(w.size);
      const width = p.w ?? d.w;
      const height = p.h ?? d.h;
      let x = p.x, y = p.y;
      if (x == null || y == null) {
        // Auto-layout : wrap si dépasse 12 cols
        if (cursorX + width > 12) { cursorX = 0; cursorY += 2; }
        x = cursorX; y = cursorY;
        cursorX += width;
      }
      return { i: w.key, x, y, w: width, h: height, minW: 2, minH: 2 };
    });
  }, [displayCatalog, prefs]);

  const handleLayoutChange = (newLayout) => {
    if (readOnly || !onLayoutChange) return;
    const next = { ...prefs };
    let order = 0;
    // Réordonne selon la position visuelle (y puis x)
    const sortedByPos = [...newLayout].sort((a, b) => (a.y - b.y) || (a.x - b.x));
    sortedByPos.forEach((item) => {
      next[item.i] = {
        enabled: !!enabledMap[item.i],
        order: order++,
        x: item.x, y: item.y, w: item.w, h: item.h,
      };
    });
    // S'assure que les widgets non-présents dans le layout gardent leurs prefs
    catalog.forEach((w) => {
      if (!next[w.key]) {
        next[w.key] = prefs[w.key] || { enabled: !!enabledMap[w.key], order: order++ };
      }
    });
    onLayoutChange(next);
  };

  return (
    <GridLayout
      className="widget-grid"
      layouts={{ lg: layout, md: layout, sm: layout, xs: layout, xxs: layout }}
      breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
      cols={{ lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }}
      rowHeight={80}
      isDraggable={!readOnly}
      isResizable={!readOnly}
      draggableHandle=".widget-drag-handle"
      compactType="vertical"
      margin={[12, 12]}
      onLayoutChange={handleLayoutChange}
    >
      {displayCatalog.map((w) => (
        <div key={w.key} data-testid={`${testidPrefix}-widget-${w.key}`}>
          <WidgetPanel
            widget={w}
            enabled={!!enabledMap[w.key]}
            readOnly={readOnly}
            onToggle={() => onToggle?.(w.key)}
            renderBody={renderBody}
            testidPrefix={testidPrefix}
          />
        </div>
      ))}
    </GridLayout>
  );
}

function WidgetPanel({ widget, enabled, readOnly, onToggle, renderBody, testidPrefix }) {
  return (
    <div
      className={`h-full w-full bg-white border ${enabled ? "border-[#DEE2E6]" : "border-dashed border-[#DEE2E6] opacity-60"} flex flex-col relative overflow-hidden ${readOnly ? "p-4" : "p-3"}`}
    >
      {!readOnly && (
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <button
            className="widget-drag-handle p-1 -m-1 cursor-grab active:cursor-grabbing text-[#868E96] hover:text-[#0A0A0A] touch-none"
            aria-label={`Glisser ${widget.label}`}
            data-testid={`${testidPrefix}-widget-drag-${widget.key}`}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onToggle}
            data-testid={`${testidPrefix}-widget-toggle-${widget.key}`}
            className={`inline-flex items-center gap-1 h-7 px-2 border text-[10px] uppercase tracking-[0.2em] font-bold transition-colors ${enabled ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-[#DEE2E6] bg-white text-[#495057] hover:border-[#0A0A0A]"}`}
          >
            {enabled ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
            {enabled ? "ON" : "OFF"}
          </button>
        </div>
      )}
      <div className="flex-1 flex flex-col overflow-auto min-h-0">
        {renderBody ? renderBody(widget) : (
          <>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#868E96] font-semibold mb-1">
              {widget.label}
            </div>
            <div className="text-xs text-[#495057]">
              {widget.category} · Taille par défaut : {widget.size}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
